import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  Upload,
  type UploadFile,
} from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FileImageOutlined,
  PlusOutlined,
  SoundOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Paged } from "../../services/api";
import { useAuth } from "../../services/auth";
import { fmtBytes, fmtDateTime, StatusTag, Tags } from "../../components/format";

type RecordingKind = "full_song" | "singing_clip";
type AudioMode = "mixed" | "vocal_only";
type PerformanceType = "original" | "cover";
type RightsStatus = "pending" | "approved" | "rejected";
type RecordingStatus = "draft" | "published" | "archived";
type SongCategory = "all" | "original" | "cover" | "unclassified";
type SongSortMode = "updatedAt-desc" | "releaseDate-desc" | "releaseDate-asc";

interface SongAsset {
  id: string;
  mimeType: string;
  sizeBytes: number;
  durationMs: number | null;
  status: string;
}

interface SongRecording {
  id: string;
  songId: string;
  characterId: string;
  characterName: string;
  versionName: string | null;
  occurredOn: string | null;
  staff: string | null;
  kind: RecordingKind;
  audioMode: AudioMode;
  performanceType: PerformanceType | null;
  sortOrder: number;
  rightsStatus: RightsStatus;
  rightsNote: string | null;
  status: RecordingStatus;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  audio: SongAsset | null;
  cover: SongAsset | null;
  /** 仅用于当前列表的下载文件名，不来自 API。 */
  songTitle?: string;
}

interface Song {
  id: string;
  title: string;
  aliases: string[];
  originalArtists: string[];
  lyricists: string[];
  composers: string[];
  tags: string[];
  lyrics: string | null;
  createdAt: string;
  updatedAt: string;
  recordings: SongRecording[];
}

interface StorageStatus {
  uploadEnabled: boolean;
  maxAudioBytes: number;
  maxCoverBytes: number;
  acceptedAudioTypes: string[];
  acceptedCoverTypes: string[];
}

interface SongTitleConflict {
  exists: boolean;
  songId: string | null;
}

interface RecordingFields {
  versionName?: string | null;
  occurredOn?: string | null;
  staff?: string;
  kind: RecordingKind;
  audioMode: AudioMode;
  performanceType: PerformanceType;
  rightsStatus: RightsStatus;
  rightsNote?: string;
  sortOrder: number;
  reason?: string;
}

interface CreateFields extends RecordingFields {
  title: string;
  aliases?: string;
  originalArtists?: string;
  lyricists?: string;
  composers?: string;
  tags?: string;
  lyrics?: string;
}

interface SongEditFields {
  title: string;
  aliases?: string;
  originalArtists?: string;
  lyricists?: string;
  composers?: string;
  tags?: string;
  lyrics?: string;
  reason?: string;
}

const kindLabels: Record<RecordingKind, string> = {
  full_song: "完整歌曲",
  singing_clip: "清唱片段",
};

const modeLabels: Record<AudioMode, string> = {
  mixed: "人声 + 伴奏",
  vocal_only: "纯人声 / 干声",
};

const performanceLabels: Record<PerformanceType, string> = {
  original: "原创",
  cover: "翻唱",
};

const rightsColors: Record<RightsStatus, string> = {
  pending: "gold",
  approved: "green",
  rejected: "red",
};

const DEFAULT_PAGE_SIZE = 20;

const defaultRecordingValues: Partial<RecordingFields> = {
  kind: "full_song",
  audioMode: "mixed",
  rightsStatus: "approved",
  sortOrder: 0,
};

function splitList(value?: string): string[] {
  return (value ?? "")
    .split(/[，,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function joinList(value: string[]): string {
  return value.join("，");
}

function songEditValues(song: Song): SongEditFields {
  return {
    title: song.title,
    aliases: joinList(song.aliases),
    originalArtists: joinList(song.originalArtists),
    lyricists: joinList(song.lyricists),
    composers: joinList(song.composers),
    tags: joinList(song.tags),
    lyrics: song.lyrics ?? "",
    reason: "",
  };
}

function recordingEditValues(recording: SongRecording): Partial<RecordingFields> {
  return {
    versionName: recording.versionName,
    occurredOn: recording.occurredOn,
    staff: recording.staff ?? "",
    kind: recording.kind,
    audioMode: recording.audioMode,
    performanceType: recording.performanceType ?? undefined,
    rightsStatus: recording.rightsStatus,
    rightsNote: recording.rightsNote ?? "",
    sortOrder: recording.sortOrder,
    reason: "",
  };
}

function normalizedTitle(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLocaleLowerCase();
}

function formatDuration(durationMs: number | null): string {
  if (!durationMs) return "—";
  const seconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

function audioDownloadName(recording: SongRecording): string {
  const baseName = [recording.songTitle, recording.versionName]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value))
    .join(" - ") || recording.songTitle || recording.id;
  const safeName = baseName
    .replace(/[<>:"/\\|?*\u0000-\u001F]/gu, "_")
    .replace(/[.\s]+$/u, "") || recording.id;
  return `${safeName}.mp3`;
}

function appendRecordingFields(formData: FormData, values: RecordingFields) {
  if (values.versionName?.trim()) formData.set("versionName", values.versionName.trim());
  formData.set("occurredOn", values.occurredOn?.trim() ?? "");
  formData.set("staff", values.staff?.trim() ?? "");
  formData.set("kind", values.kind);
  formData.set("audioMode", values.audioMode);
  formData.set("performanceType", values.performanceType);
  formData.set("rightsStatus", values.rightsStatus);
  formData.set("rightsNote", values.rightsNote?.trim() ?? "");
  formData.set("sortOrder", String(values.sortOrder ?? 0));
  if (values.reason?.trim()) formData.set("reason", values.reason.trim());
}

function RecordingFormItems() {
  return (
    <>
      <Form.Item
        name="versionName"
        label="版本名（可选）"
      >
        <Input placeholder="例如：正式版、纯人声版、2026 重制版" maxLength={80} />
      </Form.Item>
      <Form.Item
        name="performanceType"
        label="演唱归类"
        rules={[{ required: true, message: "请选择原创或翻唱" }]}
        extra="描述当前角色的这个录音版本，不会写进原曲标签。"
      >
        <Select
          placeholder="请选择"
          options={Object.entries(performanceLabels).map(([value, label]) => ({ value, label }))}
        />
      </Form.Item>
      <Space align="start" size={12} style={{ display: "flex" }}>
        <Form.Item name="occurredOn" label="发布日期" style={{ flex: "0 0 180px" }}>
          <Input type="date" />
        </Form.Item>
        <Form.Item
          name="staff"
          label="Staff（原始署名）"
          style={{ flex: 1 }}
        >
          <Input placeholder="例如：混音：少年E；曲绘：白衣酒" maxLength={10_000} />
        </Form.Item>
      </Space>
      <Space align="start" size={12} style={{ display: "flex" }}>
        <Form.Item
          name="kind"
          label="用途"
          rules={[{ required: true }]}
          style={{ flex: "1.15 1 0", minWidth: 0 }}
        >
          <Select
            style={{ width: "100%" }}
            options={Object.entries(kindLabels).map(([value, label]) => ({ value, label }))}
          />
        </Form.Item>
        <Form.Item
          name="audioMode"
          label="声音构成"
          rules={[{ required: true }]}
          style={{ flex: "1.35 1 0", minWidth: 0 }}
        >
          <Select
            style={{ width: "100%" }}
            options={Object.entries(modeLabels).map(([value, label]) => ({ value, label }))}
          />
        </Form.Item>
        <Form.Item
          name="sortOrder"
          label="默认排序"
          style={{ flex: "0.8 1 0", minWidth: 0 }}
        >
          <InputNumber min={-10_000} max={10_000} style={{ width: "100%" }} />
        </Form.Item>
      </Space>
      <Form.Item name="rightsStatus" label="版权状态" rules={[{ required: true }]}>
        <Select
          options={[
            { value: "pending", label: "待确认（不能发布）" },
            { value: "approved", label: "已通过" },
            { value: "rejected", label: "不允许使用" },
          ]}
        />
      </Form.Item>
      <Form.Item name="rightsNote" label="版权备注">
        <Input.TextArea rows={2} maxLength={500} placeholder="授权来源、使用范围或拒绝原因" />
      </Form.Item>
      <Form.Item
        name="reason"
        label="操作理由（可选）"
      >
        <Input.TextArea rows={2} maxLength={200} placeholder="如填写，会进入审计日志" />
      </Form.Item>
    </>
  );
}

const AUDIO_PREVIEW_EVENT = "dreamverse:admin-audio-preview";

function AudioActions({ recording }: { recording: SongRecording }) {
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stopPlayback = () => {
    audioRef.current?.pause();
    if (audioRef.current) audioRef.current.currentTime = 0;
    setOpen(false);
  };

  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url);
  }, [url]);

  useEffect(() => {
    const stopOtherPreview = (event: Event) => {
      const activeId = (event as CustomEvent<{ recordingId: string }>).detail?.recordingId;
      if (activeId && activeId !== recording.id) stopPlayback();
    };
    window.addEventListener(AUDIO_PREVIEW_EVENT, stopOtherPreview);
    return () => window.removeEventListener(AUDIO_PREVIEW_EVENT, stopOtherPreview);
  }, [recording.id]);

  const announcePlayback = () => {
    window.dispatchEvent(new CustomEvent(AUDIO_PREVIEW_EVENT, {
      detail: { recordingId: recording.id },
    }));
  };

  const loadAudio = async (): Promise<string> => {
    if (url) return url;
    const blob = await api.blob(`/api/v1/admin/songs/recordings/${recording.id}/audio`);
    const nextUrl = URL.createObjectURL(blob);
    setUrl(nextUrl);
    return nextUrl;
  };

  const preview = async () => {
    announcePlayback();
    setOpen(true);
    if (url) return;
    setLoading(true);
    try {
      await loadAudio();
    } catch (error) {
      message.error((error as Error).message);
      stopPlayback();
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const downloadUrl = await loadAudio();
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = audioDownloadName(recording);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Button type="link" size="small" icon={<SoundOutlined />} onClick={() => void preview()}>
        试听
      </Button>
      <Button
        type="link"
        size="small"
        icon={<DownloadOutlined />}
        loading={downloading}
        onClick={() => void download()}
      >
        下载
      </Button>
      <Modal
        open={open}
        title={`${recording.versionName ?? "录音"} · 试听`}
        footer={<Button onClick={stopPlayback}>停止并关闭</Button>}
        onCancel={stopPlayback}
        destroyOnClose
      >
        {loading ? (
          <Typography.Text type="secondary">正在加载音频…</Typography.Text>
        ) : url ? (
          <audio
            ref={audioRef}
            src={url}
            controls
            autoPlay
            onPlay={announcePlayback}
            style={{ width: "100%" }}
          />
        ) : null}
      </Modal>
    </>
  );
}

function UploadFields({
  audioFiles,
  coverFiles,
  onAudio,
  onCover,
  maxAudioBytes,
  audioRequired = true,
}: {
  audioFiles: UploadFile[];
  coverFiles: UploadFile[];
  onAudio: (files: UploadFile[]) => void;
  onCover: (files: UploadFile[]) => void;
  maxAudioBytes?: number;
  audioRequired?: boolean;
}) {
  return (
    <Space align="start" size={24} style={{ marginBottom: 16 }}>
      <div>
        <Typography.Text strong>MP3 音频（{audioRequired ? "必选" : "可选，可稍后上传"}）</Typography.Text>
        <Upload
          accept="audio/mpeg,.mp3"
          beforeUpload={() => false}
          maxCount={1}
          fileList={audioFiles}
          onChange={({ fileList }) => onAudio(fileList.slice(-1))}
        >
          <Button icon={<UploadOutlined />}>选择音频</Button>
        </Upload>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          上限 {fmtBytes(maxAudioBytes)}，服务端会校验格式并读取时长
        </Typography.Text>
      </div>
      <div>
        <Typography.Text strong>封面（可选）</Typography.Text>
        <Upload
          accept="image/png,image/jpeg,image/webp"
          beforeUpload={() => false}
          maxCount={1}
          fileList={coverFiles}
          onChange={({ fileList }) => onCover(fileList.slice(-1))}
        >
          <Button icon={<FileImageOutlined />}>选择封面</Button>
        </Upload>
      </div>
    </Space>
  );
}

function DeleteSongButton({
  song,
  onDelete,
}: {
  song: Song;
  onDelete: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  return (
    <Popconfirm
      title={`删除《${song.title}》？`}
      description={
        <Space direction="vertical" style={{ width: 320 }}>
          <span style={{ fontSize: 12 }}>
            将删除当前角色的 {song.recordings.length} 个录音版本及其音频；仍被其他录音使用的封面不会删除。
          </span>
          <Input.TextArea
            rows={2}
            maxLength={200}
            placeholder="删除理由（必填，会进入审计日志）"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Space>
      }
      okText="删除歌曲"
      cancelText="取消"
      okButtonProps={{ danger: true, disabled: reason.trim().length === 0 }}
      onOpenChange={(open) => {
        if (!open) setReason("");
      }}
      onConfirm={() => onDelete(reason.trim())}
    >
      <Button danger size="small" icon={<DeleteOutlined />}>
        删除歌曲
      </Button>
    </Popconfirm>
  );
}

export function SongLibraryPanel({ characterId }: { characterId: string }) {
  const { message, modal } = App.useApp();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const canWrite = can("song.write");
  const canPublish = can("song.publish");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [missingAudio, setMissingAudio] = useState(false);
  const [missingReleaseDate, setMissingReleaseDate] = useState(false);
  const [missingLyrics, setMissingLyrics] = useState(false);
  const [category, setCategory] = useState<SongCategory>("all");
  const [sortMode, setSortMode] = useState<SongSortMode>("updatedAt-desc");
  const [sort, order] = sortMode.split("-") as ["updatedAt" | "releaseDate", "asc" | "desc"];

  const songs = useQuery({
    queryKey: [
      "songs",
      characterId,
      page,
      pageSize,
      search,
      missingAudio,
      missingReleaseDate,
      missingLyrics,
      category,
      sortMode,
    ],
    queryFn: () =>
      api.get<Paged<Song>>("/api/v1/admin/songs", {
        characterId,
        page,
        pageSize,
        search: search || undefined,
        missingAudio,
        missingReleaseDate,
        missingLyrics,
        category,
        sort,
        order,
      }),
    placeholderData: keepPreviousData,
  });
  const storage = useQuery({
    queryKey: ["song-storage"],
    queryFn: () => api.get<StorageStatus>("/api/v1/admin/songs/storage"),
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm<CreateFields>();
  const [createAudio, setCreateAudio] = useState<UploadFile[]>([]);
  const [createCover, setCreateCover] = useState<UploadFile[]>([]);

  const [addSong, setAddSong] = useState<Song | null>(null);
  const [addForm] = Form.useForm<RecordingFields>();
  const [addAudio, setAddAudio] = useState<UploadFile[]>([]);
  const [addCover, setAddCover] = useState<UploadFile[]>([]);

  const [editSong, setEditSong] = useState<Song | null>(null);
  const [songForm] = Form.useForm<SongEditFields>();

  const [editRecording, setEditRecording] = useState<SongRecording | null>(null);
  const [recordingForm] = Form.useForm<RecordingFields>();

  const [replace, setReplace] = useState<{
    recording: SongRecording;
    kind: "audio" | "cover";
  } | null>(null);
  const [replaceForm] = Form.useForm<{ reason?: string }>();
  const [replaceFiles, setReplaceFiles] = useState<UploadFile[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [characterId]);

  useEffect(() => {
    const total = songs.data?.total;
    if (total === undefined) return;
    const lastPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > lastPage) setPage(lastPage);
  }, [page, pageSize, songs.data?.total]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["songs", characterId] });
  };

  const duplicateTitle = async (title: string, excludeSongId?: string): Promise<boolean> => {
    const result = await api.get<SongTitleConflict>("/api/v1/admin/songs/title-conflict", {
      characterId,
      title: normalizedTitle(title),
      excludeSongId,
    });
    return result.exists;
  };

  const confirmDuplicateTitle = (title: string): Promise<boolean> =>
    new Promise((resolve) => {
      modal.confirm({
        title: `已经有一首《${title}》`,
        content:
          "如果这是同一首作品，请取消并在已有歌曲中点“添加版本”；只有确实是同名的另一首歌时才继续。",
        okText: "仍然保存同名歌曲",
        cancelText: "取消",
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  const openCreate = () => {
    createForm.resetFields();
    createForm.setFieldsValue(defaultRecordingValues);
    setCreateAudio([]);
    setCreateCover([]);
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    const values = await createForm.validateFields();
    const audio = createAudio[0]?.originFileObj;
    let duplicate: boolean;
    try {
      duplicate = await duplicateTitle(values.title);
    } catch (error) {
      message.error((error as Error).message);
      return;
    }
    const allowDuplicateTitle = duplicate
      ? await confirmDuplicateTitle(values.title)
      : false;
    if (duplicate && !allowDuplicateTitle) return;
    const formData = new FormData();
    formData.set("characterId", characterId);
    formData.set("title", values.title);
    formData.set("aliases", JSON.stringify(splitList(values.aliases)));
    formData.set("originalArtists", JSON.stringify(splitList(values.originalArtists)));
    formData.set("lyricists", JSON.stringify(splitList(values.lyricists)));
    formData.set("composers", JSON.stringify(splitList(values.composers)));
    formData.set("tags", JSON.stringify(splitList(values.tags)));
    formData.set("lyrics", values.lyrics?.trim() ?? "");
    formData.set("allowDuplicateTitle", String(allowDuplicateTitle));
    appendRecordingFields(formData, values);
    if (audio) formData.set("audio", audio);
    const cover = createCover[0]?.originFileObj;
    if (cover) formData.set("cover", cover);
    setSaving(true);
    try {
      await api.postForm("/api/v1/admin/songs", formData);
      message.success(
        !audio
          ? "歌曲资料已加入曲库，录音可稍后上传"
          : values.rightsStatus === "approved"
            ? "歌曲和首个录音版本已创建并发布"
            : "歌曲和首个录音版本已保存为草稿",
      );
      setCreateOpen(false);
      await refresh();
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const openAdd = (song: Song) => {
    addForm.resetFields();
    addForm.setFieldsValue(defaultRecordingValues);
    setAddAudio([]);
    setAddCover([]);
    setAddSong(song);
  };

  const submitAdd = async () => {
    if (!addSong) return;
    const values = await addForm.validateFields();
    const audio = addAudio[0]?.originFileObj;
    if (!audio) {
      message.error("请选择 MP3 音频");
      return;
    }
    const formData = new FormData();
    formData.set("characterId", characterId);
    appendRecordingFields(formData, values);
    formData.set("audio", audio);
    const cover = addCover[0]?.originFileObj;
    if (cover) formData.set("cover", cover);
    setSaving(true);
    try {
      await api.postForm(`/api/v1/admin/songs/${addSong.id}/recordings`, formData);
      message.success(
        values.rightsStatus === "approved" ? "录音版本已添加并发布" : "录音版本已保存为草稿",
      );
      setAddSong(null);
      await refresh();
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const openSongEdit = (song: Song) => setEditSong(song);

  const copyLyrics = async (song: Song) => {
    const lyrics = song.lyrics?.trim();
    if (!lyrics) return;
    try {
      await navigator.clipboard.writeText(lyrics);
      message.success(`《${song.title}》歌词已复制`);
    } catch {
      message.error("复制失败，请手动选择歌词复制");
    }
  };

  const submitSongEdit = async () => {
    if (!editSong) return;
    const values = await songForm.validateFields();
    let duplicate: boolean;
    try {
      duplicate = await duplicateTitle(values.title, editSong.id);
    } catch (error) {
      message.error((error as Error).message);
      return;
    }
    const allowDuplicateTitle = duplicate
      ? await confirmDuplicateTitle(values.title)
      : false;
    if (duplicate && !allowDuplicateTitle) return;
    setSaving(true);
    try {
      await api.patch(`/api/v1/admin/songs/${editSong.id}`, {
        title: values.title,
        aliases: splitList(values.aliases),
        originalArtists: splitList(values.originalArtists),
        lyricists: splitList(values.lyricists),
        composers: splitList(values.composers),
        tags: splitList(values.tags),
        lyrics: values.lyrics?.trim() || null,
        allowDuplicateTitle,
        reason: values.reason?.trim() || undefined,
      });
      message.success("歌曲资料已保存");
      setEditSong(null);
      await refresh();
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const removeSong = async (song: Song, reason: string): Promise<void> => {
    try {
      await api.del(`/api/v1/admin/songs/${song.id}`, { characterId, reason });
      message.success(`《${song.title}》已从当前角色曲库删除`);
      await refresh();
    } catch (error) {
      message.error((error as Error).message);
      throw error;
    }
  };

  const openRecordingEdit = (recording: SongRecording) => setEditRecording(recording);

  const submitRecordingEdit = async () => {
    if (!editRecording) return;
    const values = await recordingForm.validateFields();
    setSaving(true);
    try {
      await api.patch(`/api/v1/admin/songs/recordings/${editRecording.id}`, values);
      message.success("录音版本已保存");
      setEditRecording(null);
      await refresh();
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const openReplace = (recording: SongRecording, kind: "audio" | "cover") => {
    replaceForm.resetFields();
    setReplaceFiles([]);
    setReplace({ recording, kind });
  };

  const submitReplace = async () => {
    if (!replace) return;
    const values = await replaceForm.validateFields();
    const file = replaceFiles[0]?.originFileObj;
    if (!file) {
      message.error(`请选择${replace.kind === "audio" ? "音频" : "封面"}文件`);
      return;
    }
    const formData = new FormData();
    formData.set(replace.kind, file);
    if (values.reason?.trim()) formData.set("reason", values.reason.trim());
    setSaving(true);
    try {
      await api.postForm(
        `/api/v1/admin/songs/recordings/${replace.recording.id}/${replace.kind}`,
        formData,
      );
      message.success(replace.kind === "audio" ? "音频已替换" : "封面已替换");
      setReplace(null);
      await refresh();
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = (recording: SongRecording, status: "draft" | "published") => {
    let reason = "";
    modal.confirm({
      title: status === "published" ? "发布这个录音版本？" : "将这个录音版本下线？",
      content: (
        <Input.TextArea
          rows={3}
          maxLength={200}
          placeholder="操作理由可选；如填写会进入审计日志"
          onChange={(event) => {
            reason = event.target.value;
          }}
        />
      ),
      okText: status === "published" ? "发布" : "下线",
      okButtonProps: { danger: status === "draft" },
      onOk: async () => {
        await api.patch(`/api/v1/admin/songs/recordings/${recording.id}/status`, {
          status,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        });
        message.success(status === "published" ? "已发布" : "已下线");
        await refresh();
      },
    });
  };

  const recordingColumns = useMemo(
    () => [
      {
        title: "版本",
        key: "version",
        render: (_: unknown, recording: SongRecording) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{recording.versionName || "未命名版本"}</Typography.Text>
            <Space size={4} wrap>
              {recording.performanceType ? (
                <Tag color={recording.performanceType === "original" ? "purple" : "blue"}>
                  {performanceLabels[recording.performanceType]}
                </Tag>
              ) : (
                <Tag>未分类</Tag>
              )}
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {kindLabels[recording.kind]} · {modeLabels[recording.audioMode]}
              </Typography.Text>
            </Space>
          </Space>
        ),
      },
      {
        title: "日期 / Staff",
        key: "facts",
        width: 220,
        render: (_: unknown, recording: SongRecording) => (
          <Space direction="vertical" size={0}>
            <span>{recording.occurredOn ?? "日期未知"}</span>
            <Typography.Text type="secondary" ellipsis={{ tooltip: recording.staff }} style={{ maxWidth: 200, fontSize: 12 }}>
              {recording.staff || "—"}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: "音频",
        key: "audio",
        width: 135,
        render: (_: unknown, recording: SongRecording) => (
          <Space direction="vertical" size={0}>
            <span>{recording.audio ? formatDuration(recording.audio.durationMs) : "未上传"}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {recording.audio ? fmtBytes(recording.audio.sizeBytes) : "不可播放"}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: "版权",
        dataIndex: "rightsStatus",
        width: 105,
        render: (value: RightsStatus) => <Tag color={rightsColors[value]}>{value}</Tag>,
      },
      {
        title: "状态",
        dataIndex: "status",
        width: 100,
        render: (value: string) => <StatusTag value={value} />,
      },
      {
        title: "更新时间",
        dataIndex: "updatedAt",
        width: 170,
        render: fmtDateTime,
      },
      {
        title: "操作",
        key: "actions",
        width: 380,
        render: (_: unknown, recording: SongRecording) => (
          <Space size={0} wrap>
            {recording.audio ? <AudioActions recording={recording} /> : null}
            {canWrite && (
              <>
                <Button type="link" size="small" onClick={() => openRecordingEdit(recording)}>
                  编辑
                </Button>
                <Button type="link" size="small" onClick={() => openReplace(recording, "audio")}>
                  {recording.audio ? "换音频" : "上传音频"}
                </Button>
                <Button type="link" size="small" onClick={() => openReplace(recording, "cover")}>
                  换封面
                </Button>
              </>
            )}
            {canPublish && recording.status !== "archived" && (
              <Button
                type="link"
                size="small"
                danger={recording.status === "published"}
                disabled={!recording.audio && recording.status !== "published"}
                onClick={() =>
                  changeStatus(recording, recording.status === "published" ? "draft" : "published")
                }
              >
                {recording.status === "published" ? "下线" : "发布"}
              </Button>
            )}
          </Space>
        ),
      },
    ],
    [canPublish, canWrite],
  );

  if (songs.error) {
    return <Alert type="error" showIcon message={(songs.error as Error).message} />;
  }

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Alert
        showIcon
        type="info"
        message="作品和录音版本分开管理"
        description="Song 保存歌名、词曲署名和通用标签；原创/翻唱归到具体录音版本。曲目不需要先上传 MP3。只有存在文件、状态已发布且版权通过的录音才会进入播放器。发布日期、Staff 和歌词会进入知识库。"
      />
      {storage.data && !storage.data.uploadEnabled && (
        <Alert
          showIcon
          type="warning"
          message="对象存储尚未配置，暂时不能上传音频"
          description="已有歌曲资料仍可查看；配置 OSS 并重启 API 后，上传按钮会自动恢复。"
        />
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Space wrap>
          {canWrite && canPublish && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
            >
              新增曲目
            </Button>
          )}
          <Input.Search
            value={searchInput}
            allowClear
            enterButton="搜索"
            loading={songs.isFetching}
            placeholder="搜索歌名、原唱、词曲、标签或歌词"
            style={{ width: 360, maxWidth: "100%" }}
            onChange={(event) => {
              const value = event.target.value;
              setSearchInput(value);
              if (value === "") {
                setSearch("");
                setPage(1);
              }
            }}
            onSearch={(value) => {
              setSearch(value.trim());
              setPage(1);
            }}
          />
        </Space>
        <Space wrap>
          <Typography.Text type="secondary">筛选：</Typography.Text>
          <Button
            type={missingAudio ? "primary" : "default"}
            aria-pressed={missingAudio}
            onClick={() => {
              setMissingAudio((value) => !value);
              setPage(1);
            }}
          >
            无音频
          </Button>
          <Button
            type={missingLyrics ? "primary" : "default"}
            aria-pressed={missingLyrics}
            onClick={() => {
              setMissingLyrics((value) => !value);
              setPage(1);
            }}
          >
            无歌词
          </Button>
          <Button
            type={missingReleaseDate ? "primary" : "default"}
            aria-pressed={missingReleaseDate}
            onClick={() => {
              setMissingReleaseDate((value) => !value);
              setPage(1);
            }}
          >
            无发布日期
          </Button>
          <Segmented<SongCategory>
            value={category}
            options={[
              { value: "all", label: "全部" },
              { value: "original", label: "原创" },
              { value: "cover", label: "翻唱" },
              { value: "unclassified", label: "未分类" },
            ]}
            onChange={(value) => {
              setCategory(value);
              setPage(1);
            }}
          />
          <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
            排序：
          </Typography.Text>
          <Select<SongSortMode>
            value={sortMode}
            style={{ width: 190 }}
            options={[
              { value: "updatedAt-desc", label: "最近更新" },
              { value: "releaseDate-desc", label: "发布日期（新 → 旧）" },
              { value: "releaseDate-asc", label: "发布日期（旧 → 新）" },
            ]}
            onChange={(value) => {
              setSortMode(value);
              setPage(1);
            }}
          />
        </Space>
      </div>

      <Spin spinning={songs.isFetching}>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {(songs.data?.items ?? []).map((song) => (
            <Card
              key={song.id}
              size="small"
              title={
                <Space>
                  <span>{song.title}</span>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {song.recordings.length} 个版本
                  </Typography.Text>
                </Space>
              }
              extra={
                canWrite ? (
                  <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openSongEdit(song)}>
                      编辑作品资料
                    </Button>
                    {canPublish ? (
                      <Button
                        size="small"
                        icon={<PlusOutlined />}
                        disabled={storage.data?.uploadEnabled === false}
                        onClick={() => openAdd(song)}
                      >
                        添加版本
                      </Button>
                    ) : null}
                    {canPublish ? (
                      <DeleteSongButton
                        song={song}
                        onDelete={(reason) => removeSong(song, reason)}
                      />
                    ) : null}
                  </Space>
                ) : null
              }
            >
              <Descriptions size="small" column={3} style={{ marginBottom: 12 }}>
                <Descriptions.Item label="原唱">
                  <Tags values={song.originalArtists} />
                </Descriptions.Item>
                <Descriptions.Item label="词曲">
                  {[...song.lyricists, ...song.composers].join(" / ") || "—"}
                </Descriptions.Item>
                <Descriptions.Item label="歌词">
                  {song.lyrics?.trim() ? (
                    <Space size={4} align="start">
                      <span>
                        {song.lyrics.trim().slice(0, 80)}
                        {song.lyrics.trim().length > 80 ? "…" : ""}
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => void copyLyrics(song)}
                        style={{ paddingInline: 4, height: "auto" }}
                      >
                        复制
                      </Button>
                    </Space>
                  ) : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="标签">
                  <Tags values={song.tags} />
                </Descriptions.Item>
              </Descriptions>
              <Table<SongRecording>
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={song.recordings.map((recording) => ({
                  ...recording,
                  songTitle: song.title,
                }))}
                columns={recordingColumns}
                scroll={{ x: 1040 }}
              />
            </Card>
          ))}

          {!songs.isLoading && (songs.data?.items.length ?? 0) === 0 && (
            <Card size="small">
              <Typography.Text type="secondary">
                {search || missingAudio || missingReleaseDate || missingLyrics || category !== "all"
                  ? "没有符合当前搜索或筛选条件的曲目。"
                  : "这个角色还没有录入演唱作品。"}
              </Typography.Text>
            </Card>
          )}
        </Space>
      </Spin>

      {(songs.data?.total ?? 0) > 0 && (
        <Pagination
          current={page}
          pageSize={pageSize}
          total={songs.data?.total ?? 0}
          showSizeChanger
          pageSizeOptions={[10, 20, 50]}
          showTotal={(total, range) => `${range[0]}-${range[1]} / 共 ${total} 首`}
          onChange={(nextPage, nextPageSize) => {
            if (nextPageSize !== pageSize) {
              setPageSize(nextPageSize);
              setPage(1);
              return;
            }
            setPage(nextPage);
          }}
          style={{ textAlign: "center" }}
        />
      )}

      <Modal
        open={createOpen}
        title="新增曲目"
        width={720}
        okText="创建"
        confirmLoading={saving}
        onOk={submitCreate}
        onCancel={() => setCreateOpen(false)}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={defaultRecordingValues}
          preserve={false}
        >
          <Form.Item name="title" label="歌名" rules={[{ required: true, message: "请填写歌名" }]}>
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item name="aliases" label="别名">
            <Input placeholder="用逗号分隔" />
          </Form.Item>
          <Space align="start" size={12} style={{ display: "flex" }}>
            <Form.Item name="originalArtists" label="原唱" style={{ flex: 1 }}>
              <Input placeholder="用逗号分隔" />
            </Form.Item>
            <Form.Item name="lyricists" label="作词" style={{ flex: 1 }}>
              <Input placeholder="用逗号分隔" />
            </Form.Item>
            <Form.Item name="composers" label="作曲" style={{ flex: 1 }}>
              <Input placeholder="用逗号分隔" />
            </Form.Item>
          </Space>
          <Form.Item name="tags" label="通用标签">
            <Input placeholder="曲风、语言、场景等，用逗号分隔" />
          </Form.Item>
          <Form.Item
            name="lyrics"
            label="歌词（保存后自动进入知识库）"
            extra="这里只保存聊天召回用的简单歌词正文，不区分录音版本。"
          >
            <Input.TextArea rows={8} maxLength={100_000} showCount />
          </Form.Item>
          <UploadFields
            audioFiles={createAudio}
            coverFiles={createCover}
            onAudio={setCreateAudio}
            onCover={setCreateCover}
            maxAudioBytes={storage.data?.maxAudioBytes}
            audioRequired={false}
          />
          <RecordingFormItems />
        </Form>
      </Modal>

      <Modal
        open={Boolean(addSong)}
        title={`给《${addSong?.title ?? ""}》添加录音版本`}
        width={680}
        okText="添加"
        confirmLoading={saving}
        onOk={submitAdd}
        onCancel={() => setAddSong(null)}
        destroyOnClose
      >
        <Form
          form={addForm}
          layout="vertical"
          initialValues={defaultRecordingValues}
          preserve={false}
        >
          <UploadFields
            audioFiles={addAudio}
            coverFiles={addCover}
            onAudio={setAddAudio}
            onCover={setAddCover}
            maxAudioBytes={storage.data?.maxAudioBytes}
          />
          <RecordingFormItems />
        </Form>
      </Modal>

      <Modal
        open={Boolean(editSong)}
        title={`编辑《${editSong?.title ?? ""}》`}
        okText="保存"
        confirmLoading={saving}
        onOk={submitSongEdit}
        onCancel={() => setEditSong(null)}
        afterOpenChange={(open) => {
          if (!open || !editSong) return;
          songForm.resetFields();
          songForm.setFieldsValue(songEditValues(editSong));
        }}
        destroyOnClose
      >
        <Form form={songForm} layout="vertical" preserve={false}>
          <Form.Item name="title" label="歌名" rules={[{ required: true }]}>
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item name="aliases" label="别名">
            <Input placeholder="用逗号分隔" />
          </Form.Item>
          <Form.Item name="originalArtists" label="原唱">
            <Input placeholder="用逗号分隔" />
          </Form.Item>
          <Form.Item name="lyricists" label="作词">
            <Input placeholder="用逗号分隔" />
          </Form.Item>
          <Form.Item name="composers" label="作曲">
            <Input placeholder="用逗号分隔" />
          </Form.Item>
          <Form.Item name="tags" label="通用标签">
            <Input placeholder="曲风、语言、场景等，用逗号分隔" />
          </Form.Item>
          <Form.Item
            name="lyrics"
            label="歌词（保存后自动进入知识库）"
            extra="修改后会生成一个知识草稿版本并自动发布，不需要去知识库重复录入。"
          >
            <Input.TextArea rows={10} maxLength={100_000} showCount />
          </Form.Item>
          <Form.Item name="reason" label="操作理由（可选）">
            <Input.TextArea rows={2} maxLength={200} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={Boolean(editRecording)}
        title={`编辑录音版本${editRecording?.versionName ? ` · ${editRecording.versionName}` : ""}`}
        okText="保存"
        confirmLoading={saving}
        onOk={submitRecordingEdit}
        onCancel={() => setEditRecording(null)}
        afterOpenChange={(open) => {
          if (!open || !editRecording) return;
          recordingForm.resetFields();
          recordingForm.setFieldsValue(recordingEditValues(editRecording));
        }}
        destroyOnClose
      >
        <Form form={recordingForm} layout="vertical" preserve={false}>
          <RecordingFormItems />
        </Form>
      </Modal>

      <Modal
        open={Boolean(replace)}
        title={`${replace?.kind === "audio" ? "替换音频" : "替换封面"}${replace?.recording.versionName ? ` · ${replace.recording.versionName}` : ""}`}
        okText="上传并替换"
        confirmLoading={saving}
        onOk={submitReplace}
        onCancel={() => setReplace(null)}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          message={
            replace?.kind === "audio"
              ? "只有修复同一份录音的编码或文件错误时才替换；重新演唱或重混请新增版本。"
              : "封面 Asset ID 会保持不变，已有引用会自动生效。"
          }
          style={{ marginBottom: 16 }}
        />
        <Upload
          accept={replace?.kind === "audio" ? "audio/mpeg,.mp3" : "image/png,image/jpeg,image/webp"}
          beforeUpload={() => false}
          maxCount={1}
          fileList={replaceFiles}
          onChange={({ fileList }) => setReplaceFiles(fileList.slice(-1))}
        >
          <Button icon={<UploadOutlined />}>选择文件</Button>
        </Upload>
        <Form form={replaceForm} layout="vertical" style={{ marginTop: 16 }} preserve={false}>
          <Form.Item name="reason" label="操作理由（可选）">
            <Input.TextArea rows={2} maxLength={200} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
